import {
  DataGrid,
  useGridApiRef,
  GridActionsCellItem,
  gridClasses,
} from '@mui/x-data-grid';
import RestoreIcon from '@mui/icons-material/Restore';
import LoadingButton from '@mui/lab/LoadingButton';
import SaveIcon from '@mui/icons-material/Save';
import ErrorIcon from '@mui/icons-material/Error';
import { darken } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { get, post } from '../../axiosUtils/appUtils';
import { config } from '../../lib/envConfig';
import { useMsal } from '@azure/msal-react';
import { useState, useEffect, useMemo, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import Spinner from "../../components/Spinner/Spinner";
import { Button, Paper, Card, Chip, Tooltip } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import AccordionBox from "../../components/AccordionBox/AccordionBox";
import Toast from "../../components/Toast/Toast";
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { RGB, OLFACTIVES, FRAGRANCE_FIELDS_VALIDATION_ERROR_MESSAGE} from '../../lib/constant';



const BulkEditAssociation = forwardRef((props, ref) => {
  const apiRef = useGridApiRef();
  const [expandedRow, setExpandedRow] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  const [boxData, setBoxData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [updatedRows, setUpdatedRows] = useState([]);
  const code = config?.VITE_FUNC_KEY_CODE;
  const { accounts } = useMsal();
  const userName = accounts.length > 0 ? accounts[0].username : "Guest";
  const [hasUnsavedRows, setHasUnsavedRows] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const unsavedChangesRef = useRef({
    unsavedRows: {},
    rowsBeforeChange: {},
  });
  const [loading, setLoading] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState("success");
  const [toastOpen, setToastOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogData, setDialogData] = useState({
    dialogTitle: "Warning!",
      dialogText: "Are you sure you want to modify fragrance association details?",
    submitButtonText: " Save ",
  });
  const [totalRecords, setTotalRecords] = useState(0);

  const [showFilterApplied, setShowFilterApplied] = useState(false);
  const [filterApplied, setFiltersApplied] = useState({});
  const [rowErrors, setRowErrors] = useState({});

  
const validateRow = (row) => {
  let hasError = false;
  // Associations array must exist and be non-empty
  if (!Array.isArray(row.Associations) || row.Associations.length === 0 || !hasOtherObjects(row.Associations)) {
    hasError = true;
  }
  // RGB: at least one value in any Category[].RGB
  const hasRGB = Array.isArray(row.Category) && row.Category.some(cat => cat.RGB && cat.RGB.trim() !== "");
  if (!hasRGB) {
    hasError = true;
  }
  // Olfactives: all OLFACTIVES fields must have at least one value in Associations
  const missingOlfactives = (Array.isArray(OLFACTIVES) ? OLFACTIVES : []).filter(title => {
    const assocObj = row.Associations?.find(obj => Object.keys(obj)[0] === title);
    return !(assocObj && Array.isArray(assocObj[title]) && assocObj[title].length > 0);
  });
  if (missingOlfactives.length > 0) {
    hasError = true;
  }
  return hasError;
};
  useEffect(() => {
    const errors = {};
    updatedRows.forEach(row => {
      errors[row.id] = validateRow(row);
    });
    setRowErrors(errors);
  }, [updatedRows]);

  

  useEffect(() => {
    getAdminFilters();
    fetchAssociations(paginationModel);
  }, []);

  const handleSubmit = () => {
    setShowFilterApplied(true);
    setPaginationModel({ page: 0, pageSize: 10 });
    fetchAssociations(paginationModel);
  };


  const handleCheckboxChange = (title, item) => {
    setSelectedCategories((prev) => {
      const currentItems = prev[title] || [];
      const isSelected = currentItems.includes(item);

      const updatedItems = isSelected
        ? currentItems.filter((i) => i !== item)
        : [...currentItems, item];

      return {
        ...prev,
        [title]: updatedItems,
      };
    });
  };

  const getAdminFilters = () => {
    const url = `/getAdminFilters?library_id=1&ad_group_id=1&code=${code}&user_email=${userName}`;
    get(url)
      .then((response) => {
          setFilterData(response.data.data.AdminFilters)
      }).catch((error) => {
        setToastMsg("Something went wrong while fetching filters data");
        setToastType("error");
        setToastOpen(true);
      });
  }

  // Merge subcategories by Title, combining all values and removing duplicates
  const  mergeSubCategoriesByTitle = (input, selectedCategories) => {
    const merged = {};

    input.forEach(categoryObj => {
      if(selectedCategories.length == 0 || selectedCategories.includes(categoryObj.Category)){
        categoryObj.SubCategory.forEach(subCat => {
          const title = subCat.Title;
          if (!merged[title]) {
            merged[title] = {
              Title: title,
              Value: new Set(),
              AccessTo: new Set(),
              IDs: new Set(),
            };
          }
          subCat.Value.forEach(val => merged[title].Value.add(val));
          merged[title].AccessTo.add(subCat.AccessTo);
          merged[title].IDs.add(subCat.ID);
        });
      }
      
    });

  
  let data =  Object.values(merged).map(item => ({
      Title: item.Title,
      Value: Array.from(item.Value),
      AccessTo: Array.from(item.AccessTo),
      IDs: Array.from(item.IDs),
    }));


    setBoxData(data);
  return data
}

  const handleRGBChange = (e, id, category) => {
  let row =JSON.parse(JSON.stringify(rowData.filter(r => r.id === id)))?.[0];

    if (updatedRows?.length > 0) {
    const foundRow = updatedRows.find(r => r.id === id);
      if (foundRow) {
        row = foundRow;
      }
    }

  const updatedRow = { ...row, Category: row.Category.map(cat => {
        if (cat.Name === category) {
          return { ...cat, RGB: e.target.value };
        }
        return cat;
  })};

  setUpdatedRows(prev => {
      const existingIndex = prev.findIndex(r => r.id === updatedRow.id);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = updatedRow;
        return updated;
      }
      return [...prev, updatedRow];
    });

}

  const handleAccordion = (row) => {
  if(row.id){
      mergeSubCategoriesByTitle(filterData, row.SelectedCategories);
    }
    setExpandedRow((prev) => (prev === row.id ? null : row.id));
  setTabValue(0)
  };

  const handleCategoryChange = (e, id, val) => {
    let row =JSON.parse(JSON.stringify(rowData.filter(r => r.id === id)))?.[0];
    if (updatedRows?.length > 0) {
            const foundRow = updatedRows.find(r => r.id === id);
      if (foundRow) {
        row = foundRow;
      }
    }

          if(e.target.checked) {
      row.SelectedCategories.push(val);
            row.Category.push({'Name': val});
    } else {
            row.SelectedCategories = row.SelectedCategories.filter(cat => cat !== val);
            row.Category = row.Category.filter(cat => cat.Name !== val);
    }
    row.Associations = [];

          const updatedRow = { ...row, SelectedCategories:  row.SelectedCategories, Category: row.Category};

          const x = mergeSubCategoriesByTitle(filterData, updatedRow.SelectedCategories);

          setUpdatedRows(prev => {
              const existingIndex = prev.findIndex(r => r.id === updatedRow.id);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = updatedRow;
        return updated;
      }
      return [...prev, updatedRow];
    });
  }

  useImperativeHandle(ref, () => ({
    fetchOnSearch: () => fetchAssociations(paginationModel),
    search,
    setSearch,
  }));

  const fetchAssociations = (pageModel, reset=false) => {
    setLoading(true);
    let mode;
    let pageIndex = reset ? 1 : pageModel.page + 1;

    const filteredCategories = Object.keys(selectedCategories)
      .filter(key => selectedCategories[key]?.length > 0)
      .reduce((obj, key) => {
        obj[key] = selectedCategories[key];
        return obj;
      }, {});


    if (!reset && Object.keys(filteredCategories).length > 0 && search !== "") {
      mode = 4; // search + filters
    } else if (!reset && Object.keys(filteredCategories).length > 0) {
      mode = 3; // only filters
    } else if (search !== "") {
      mode = 2; // only search
    } else {
      mode = 1; // load all
    }

    let url = `/getBulkAssociations?code=${code}&library_id=1&user_email=${userName}&mode=${mode}&pageIndex=${pageIndex}`;

    if (!reset &&Object.keys(filteredCategories).length > 0) {
      const selectedParam = Object.fromEntries(
        Object.entries(filteredCategories).map(([key, value]) => [
          key,
          Array.isArray(value) ? value.join(",") : value,
        ])
      );
      setFiltersApplied(filteredCategories)
      url += `&values=${encodeURIComponent(JSON.stringify(selectedParam))}`;
    }

    if(mode === 2 || mode === 4) {
      url += `&searchkey=${encodeURIComponent(search)}`;
    }
    get(url)
      .then((response) => {
        let fragranceList = response?.data?.data.fragranceList;
        setTotalRecords(response?.data?.data?.TotalRecords || 0);
           fragranceList.map(each=> {
                 each.id = each.ID
          each.Associations = each.Associations || [];
                 each.SelectedCategories = each?.SelectedCategories?.includes(',') ? each?.SelectedCategories?.split(',').filter(Boolean) : [each?.SelectedCategories];
           })
        setRowData(fragranceList);
           setUpdatedRows([])
        setLoading(false);
        setExpandedRow(null);
        setTabValue(0);
        if (reset) {
          setPaginationModel({
            pageSize: 10,
            page: 0,
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        setToastMsg(error?.message);
        setToastType("error");
        setToastOpen(true);
      });
  }

  const handleAssociationChange = (e, id, title, val) => {
    let row =JSON.parse(JSON.stringify(rowData.filter(r => r.id === id)))?.[0];
    const checked = e.target.checked;
    if (updatedRows?.length > 0) {
      const foundRow = updatedRows.find(r => r.id === id);
      if (foundRow) {
        row = foundRow;
      }
    }

    // If Associations does not exist, initialize it as an empty array
    if (!row?.Associations) {
      row = { ...row, Associations: [] };
    }

    let hasTitle = row?.Associations?.some(obj => obj[title]);
    let newAssociations;

    if (hasTitle) {
      newAssociations = row?.Associations.map(obj => {
        if (obj[title]) {
          let arr = obj[title];
          if (checked) {
            if (!arr.includes(val)) arr.push(val);
          } else {
            arr = arr.filter(item => item !== val);
          }
          return { ...obj, [title]: arr };
        }
        return obj;
      });
    } else {
      // Add new association for title
      newAssociations = [
        ...row?.Associations,
        { [title]: checked ? [val] : [] }
      ];
    }
    const updatedRow = { ...row, Associations: newAssociations };

    setUpdatedRows(prev => {
      const existingIndex = prev.findIndex(r => r.id === updatedRow.id);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = updatedRow;
        return updated;
      }
      return [...prev, updatedRow];
    });
  }

  let aColumns = [
      { field: 'ID', headerName: 'ID', width: 90, editable: false, headerClassName: "custom-header", },
      { field: 'Name', headerName: 'Name', width: 200, editable: false, headerClassName: "custom-header",  },
    {
        field: 'details',
        headerName: 'Details',
      headerClassName: "custom-header",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              overflowX: "auto",
            }}
          >
            <IconButton
              size="small"
              onClick={() => handleAccordion(params.row)}
              aria-label="expand row"
              sx={{
                mr: 1,
                transform:
                  expandedRow === params.row.id
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
            {expandedRow === params.row.id && (
              <Box sx={{ pl: 4, width: "100%" }}>
                <Box sx={{ width: "100%" }}>
                  <Typography>Category</Typography>
                  <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                    {" "}
                    {filterData.map((val) => {
                        const updatedRow = updatedRows.find(row => row.id === params.row.id);
                      const selectedCategories = updatedRow
                        ? updatedRow.SelectedCategories
                        : params.row.SelectedCategories;

                      return (
                        <Typography key={val.Category}>
                          <Checkbox
                              checked={selectedCategories.includes(val.Category) || false}
                            onChange={(e) =>
                              handleCategoryChange(
                                e,
                                params.row.id,
                                val.Category
                              )
                            }
                          />
                          {val.Category}
                        </Typography>
                      );
                    })}
                  </Box>
                  <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="Associations" />
                    <Tab label="RGB" />
                    <Tab label="Olfactive Description" />
                  </Tabs>
                  <Paper
                    elevation={2}
                    sx={{
                      background: "#f9f9f9",
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      width: "max-content",
                      minHeight: 320,
                      maxHeight: "28vh",
                      // overflow: "hidden",
                    }}
                  >
                    {tabValue === 0 && (
                      <>
                        {boxData
                          .filter(
                            (each) =>
                              !OLFACTIVES.includes(each.Title)
                          )
                          .map((each) => {
                          return (
                            <Box
                              key={each.Title}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                // maxHeight: "30vh",
                                border: "1px solid blue",
                                background: "white",
                                color: "black",
                                minWidth: 200,
                                p: 1,
                              }}
                            >
                              <Typography
                                sx={{ fontWeight: "bold", color: "#005EF0" }}
                              >
                                {each.Title}
                              </Typography>
                              <Box
                                sx={{ display: "flex", flexDirection: "row" }}
                              >
                                {Array.from({
                                  length: Math.ceil(each.Value.length / 7),
                                }).map((_, colIdx) => (
                                  <Box
                                    key={colIdx}
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                    }}
                                  >
                                    {each.Value.slice(
                                      colIdx * 7,
                                      (colIdx + 1) * 7
                                    ).map((val) => {
                                      let ans = false;
                                        const updatedRow = updatedRows.find(row => row.ID === params.row.id)
                                        if(updatedRow && updatedRow?.Associations.length == 0) {
                                          ans = false
                                        }else{
                                        const found = updatedRows.find(row => row.ID === params.row.id)?.Associations.find(obj => Object.keys(obj)[0] === each.Title);                                      

                                        if(found) {
                                          ans = found[each.Title].includes(val);
                                        }else if(updatedRow && !found) {
                                               ans = false
                                        }else {
                                          const assocObj =
                                            params.row?.Associations.filter(
                                              (obj) =>
                                              Object.keys(obj)[0] === each.Title
                                            )?.[0];
                                          const assocValues = assocObj
                                            ? assocObj[each.Title]
                                            : [];
                                          ans = assocValues.includes(val);
                                        }
                                      }
                                      return (
                                        <Typography
                                          key={val}
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <Checkbox
                                              disabled={updatedRows.find(row => row.ID === params.row.id) ? updatedRows.find(row => row.ID === params.row.id)?.SelectedCategories?.length == 0 : params.row.SelectedCategories?.length == 0}
                                            size="small"
                                            checked={ans}
                                            onChange={(e) => {
                                                handleAssociationChange(e, params.row.id, each.Title, val);
                                            }}
                                          />
                                          {val}
                                        </Typography>
                                      );
                                    })}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          );
                        })}
                      </>
                    )}
                    {tabValue === 1 && (
                      <>
                        {/* RGB Tab Content */}
                        <Box
                          sx={{
                            border: "2px solid #005EF0",
                            // maxHeight: "30vh",
                            borderRadius: "8px",
                            minWidth: 300,
                            p: 2,
                          }}
                        >
                          {(() => {
                              let currentRow = updatedRows.find(row => row.id === params.row.id) ? updatedRows.find(row => row.id === params.row.id) : params.row;
                            return currentRow.Category.map((label) => {
                              // Find updated RGB value if exists in updatedRows
                                const updatedRow = updatedRows.find(row => row.id === params.row.id);
                              const updatedCategory = updatedRow
                                  ? updatedRow.Category.find(cat => cat.Name === label.Name)
                                : null;
                              const selectedRGB = updatedCategory
                                ? updatedCategory.RGB
                                : label?.RGB || "";

                              return (
                                <Box
                                  key={label?.Name}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    minWidth: 120,
                                    mb: 2,
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      mb: 1,
                                      color: "#005EF0",
                                      fontWeight: "bold",
                                      mr: 2,
                                    }}
                                  >
                                    {label?.Name}
                                  </Typography>
                                  <select
                                    style={{
                                      padding: "6px 12px",
                                      borderRadius: "4px",
                                      border: "1px solid #005EF0",
                                      color: "#005EF0",
                                      fontWeight: "bold",
                                      width: "15vw",
                                      marginLeft: "auto",
                                    }}
                                    value={selectedRGB}
                                    onChange={(e) => {
                                        handleRGBChange(e, params.row.id, label?.Name);
                                    }}
                                  >
                                    <option value="">Select RGB Colour</option>
                                    {RGB.map((option) => (
                                      <option key={option} value={option.toLowerCase()}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                </Box>
                              );
                            });
                          })()}
                        </Box>
                      </>
                    )}
                    {tabValue === 2 && (
                      <>
                        {boxData
                          .filter(
                            (each) => OLFACTIVES.includes(each.Title)
                          )
                          .map((each) => {
                            return (
                              <Box
                                key={each.Title}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                  // maxHeight: "30vh",
                                  border: "1px solid blue",
                                  background: "white",
                                  color: "black",
                                  minWidth: 200,
                                  p: 1,
                                }}
                              >
                                <Typography sx={{ fontWeight: "bold", color: "#005EF0" }}>
                                  {each.Title}
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                  {Array.from({
                                    length: Math.ceil(each.Value.length / 7),
                                  }).map((_, colIdx) => (
                                    <Box
                                      key={colIdx}
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                      }}
                                    >
                                      {each.Value.slice(colIdx * 7, (colIdx + 1) * 7).map((val) => {
                                        let ans = false;
                                        const updatedRow = updatedRows.find(
                                          (row) => row.ID === params.row.id
                                        );
                                        if (updatedRow && updatedRow?.Associations.length == 0) {
                                          ans = false;
                                        } else {
                                          const found = updatedRows
                                            .find((row) => row.ID === params.row.id)
                                            ?.Associations.find(
                                              (obj) => Object.keys(obj)[0] === each.Title
                                            );

                                          if (found) {
                                            ans = found[each.Title].includes(val);
                                          } else if (updatedRow && !found) {
                                            ans = false;
                                          } else {
                                            const assocObj = params.row?.Associations.filter(
                                              (obj) => Object.keys(obj)[0] === each.Title
                                            )?.[0];
                                            const assocValues = assocObj ? assocObj[each.Title] : [];
                                            ans = assocValues.includes(val);
                                          }
                                        }
                                        return (
                                          <Typography
                                            key={val}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <Checkbox
                                              disabled={
                                                updatedRows.find(
                                                  (row) => row.ID === params.row.id
                                                )
                                                  ? updatedRows.find(
                                                      (row) => row.ID === params.row.id
                                                    )?.SelectedCategories?.length == 0
                                                  : params.row.SelectedCategories?.length == 0
                                              }
                                              size="small"
                                              checked={ans}
                                              onChange={(e) => {
                                                handleAssociationChange(
                                                  e,
                                                  params.row.id,
                                                  each.Title,
                                                  val
                                                );
                                              }}
                                            />
                                            {val}
                                          </Typography>
                                        );
                                      })}
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            );
                          })}
                      </>
                    )}
                  </Paper>
                </Box>
              </Box>
            )}
          </Box>
        );
      },
    },
  ];

  const hasOtherObjects = (arr) => {
    return arr.some(obj => {
      const keys = Object.keys(obj);
      return keys.some(key => !OLFACTIVES.includes(key) && Array.isArray(obj[key]) && obj[key].length > 0);
    });
  }

  const columns = useMemo(() => {
    return [
      {
        field: 'actions',
        type: 'actions',
        headerClassName: "custom-header",
        getActions: ({ id, row }) => {
          // Validation logic for updated rows
          const updatedRow = updatedRows.find(r => r.id === id);
          let errorMsg = "";
          let hasError = false;
          if (updatedRow) {
            if(updatedRow.SelectedCategories.length == 0) {
              errorMsg += "At least one Category must be selected. ";
              hasError = true;
            }else {
            // Associations array must exist and be non-empty
            if (!Array.isArray(updatedRow.Associations) || updatedRow.Associations.length === 0 || !hasOtherObjects(updatedRow.Associations)) {
              errorMsg += "Associations must have at least one value. ";
              hasError = true;
            }
            // RGB: at least one value in any Category[].RGB
            const hasRGB = Array.isArray(updatedRow.Category) && updatedRow.Category.some(cat => cat.RGB && cat.RGB.trim() !== "");
            if (!hasRGB) {
              errorMsg += "At least one RGB value is required. ";
              hasError = true;
            }
            // Olfactives: all OLFACTIVES fields must have at least one value in Associations
            const missingOlfactives = (Array.isArray(OLFACTIVES) ? OLFACTIVES : []).filter(title => {
              const assocObj = updatedRow.Associations?.find(obj => Object.keys(obj)[0] === title);
              return !(assocObj && Array.isArray(assocObj[title]) && assocObj[title].length > 0);
            });
            if (missingOlfactives.length > 0) {
              errorMsg += `Missing value(s) for: ${missingOlfactives.join(", ")}. `;
              hasError = true;
            }
          }
          }
          if (hasError) {
            return [
              <Tooltip title={errorMsg} key="error-tooltip">
              <GridActionsCellItem
                key="error-icon"
                icon={<ErrorIcon sx={{ color: "red" }} />}
                label={errorMsg || "Validation error"}
              /></Tooltip>,
              <GridActionsCellItem
                key="restore-icon"
                icon={<RestoreIcon />}
                label="Discard changes"
                onClick={() => {
                  setUpdatedRows(prev => prev.filter(row => row.id !== id));
                }}
              />
            ];
          } else {
            return [
              <GridActionsCellItem
                key="restore-icon"
                icon={<RestoreIcon />}
                label="Discard changes"
                onClick={() => {
                  setUpdatedRows(prev => prev.filter(row => row.id !== id));
                }}
              />
            ];
          }
        },
      },
      ...aColumns,
    ];
  }, [aColumns, updatedRows]);

  const processRowUpdate = useCallback((newRow, oldRow) => {
    const rowId = newRow.id;

    unsavedChangesRef.current.unsavedRows[rowId] = newRow;
    if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
      unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow;
    }
    setHasUnsavedRows(true);
    return newRow;
  }, []);


      const saveChanges =  () => {
      let url = `/updateBulkAssociations?code=${code}&library_id=1&user_email=${userName}`;
      setLoading(true);
      setConfirmOpen(false);
      let isRGB = true;
      let isOlfactive = true;
      const prevValues = updatedRows.map(row => {
        const original = rowData
          .filter(r => {
            const rgbCheck = row?.Category.some(obj => obj.RGB && obj.RGB.trim() !== "");
            const olfactiveCheck = OLFACTIVES?.every(title => {
              const assocObj = row?.Associations?.find(obj => Object.keys(obj)[0] === title);
              return assocObj && Array.isArray(assocObj[title]) && assocObj[title].length > 0;
            });
            if(rgbCheck && olfactiveCheck){
              return r.id === row.id;
            } else if(!olfactiveCheck && !rgbCheck) {
              isRGB = false;
              isOlfactive = false;
              return;
            }else if(!olfactiveCheck) {
              isOlfactive = false;
              return;
            }else if(!rgbCheck) {
              isRGB = false;
              return;
            }
          })
          .map(({ id, ...rest }) => rest);
        return original;
      }).flat();
  
      if(!isRGB && !isOlfactive) {
        setToastMsg(FRAGRANCE_FIELDS_VALIDATION_ERROR_MESSAGE + "RGB, olfactive");
        setToastType("error");
        setToastOpen(true);
        setLoading(false);
        return;
      }else if(!isOlfactive) {
        setToastMsg(FRAGRANCE_FIELDS_VALIDATION_ERROR_MESSAGE + "Olfactive");
        setToastType("error");
        setToastOpen(true);
        setLoading(false);
        return;
      }else if(!isRGB) {
        setToastMsg(FRAGRANCE_FIELDS_VALIDATION_ERROR_MESSAGE + "RGB");
        setToastType("error");
        setToastOpen(true);
        setLoading(false);
        return;
      }
  
      const updateRowsData = updatedRows.map(({ id, ...rest }) => rest); // Remove 'id' from each updated row
  
      post(url, { products: updateRowsData, prevValues: [...prevValues] })
        .then((response) => {
          setToastMsg('Records updated successfully');
          setToastType("success");
          setToastOpen(true);
          setLoading(false);
          fetchAssociations(paginationModel);
        })
        .catch((error) => {
          setToastMsg(error?.message ? error?.message : 'Something went wrong while updating records');
          setToastType("error");
          setToastOpen(true);
          setLoading(false);
        });
    };

  const handlePagination = (pageModel) => {
    setPaginationModel(pageModel);
    fetchAssociations(pageModel);
  };

  const getRowClassName = useCallback(({ id }) => {
    const isUpdated = updatedRows.some(row => row.id === id);
    if (isUpdated) {
      return "row--edited";
    }
    return "";
  }, [updatedRows]);

  return (
    <>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          // position: "relative",
          marginBottom: "1.5rem",
          "& .actions": {
            color: "text.secondary",
          },
          "& .textPrimary": {
            color: "text.primary",
          },
        }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <>
            <Box
              style={{
                paddingBottom: "30px",
                display: "flex",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexShrink: 0,
                  paddingBottom: "20px",
                }}
              >
                <Button
                  variant="contained"
                  sx={{maxHeight: 40, minWidth: 170}}
                  onClick={() => {
                    setShowFilter((prev) => !prev);
                    setSelectedCategories({});
                    if(showFilter){
                      setShowFilterApplied(false);
                      setFiltersApplied({});
                      fetchAssociations(paginationModel, true);
                    }
                  }}
                  startIcon={<TuneIcon />}
                >
                  {showFilter ? "Reset Filter" : "Show Filter"}
                </Button>

                <Button
                  sx={{maxHeight: 40, minWidth: 170}}
                  variant="contained"
                  onClick={handleSubmit}
                  startIcon={<TuneIcon />}
                  disabled={Object.keys(selectedCategories).length === 0}
                >
                  Apply Filter
                </Button>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  maxHeight: "50px",
                  overflowY: "auto",
                  padding: 1,
                  width: "55vw",
                }}
              >
                {Object.keys(filterApplied)?.map(
                  (category, index) =>
                    filterApplied[category].length > 0 &&
                    showFilterApplied && (
                      <Chip
                        key={index}
                        label={(category=="rgb" ? "List Status" : category) + "-" + filterApplied[category]}
                        variant="outlined"
                      />
                    )
                )}
              </Box>
            </Box>
            <Box sx={{ display: "flex", height: "calc(100% - 56px)", gap: 2 }}>
              {showFilter && (
                <Box sx={{ width: "19vw"}}>
                  <Card >
                    <Paper
                      elevation={3}
                      sx={{
                        height: "80vh",
                        borderRadius: 1,
                        alignSelf: "flex-start",
                        overflowX: "hidden",
                        paddingLeft: 1
                      }}
                    >
                      <AccordionBox
                        selectedCategories={selectedCategories}
                        handleCheckboxChange={handleCheckboxChange}
                      />
                    </Paper>
                  </Card>
                </Box>
              )}
              <Box sx={{ flexGrow: 1,  height: "80vh", minWidth: "64vw" }}>
                <Card sx={{ width: showFilter ? "64vw" : "100%" }}>
                  <div style={{ marginBottom: 4 }}>
                    <Tooltip
                      title={
                        updatedRows.length === 0
                          ? "No changes to save."
                          : Object.values(rowErrors).some(Boolean)
                          ? "Please fix validation errors before saving."
                          : ""
                      }
                      disableHoverListener={
                        updatedRows.length > 0 && !Object.values(rowErrors).some(Boolean)
                      }
                    >
                      <span>
                        <LoadingButton
                          disabled={
                            updatedRows.length === 0 ||
                            Object.values(rowErrors).some(Boolean)
                          }
                          loading={isSaving}
                          onClick={() => setConfirmOpen(true)}
                          startIcon={<SaveIcon />}
                          loadingPosition="start"
                        >
                          <span>Save</span>
                        </LoadingButton>
                      </span>
                    </Tooltip>
                    <Button
                      disabled={updatedRows.length === 0}
                      onClick={() => fetchAssociations(paginationModel)}
                      startIcon={<RestoreIcon />}
                    >
                      Discard all changes
                    </Button>

                  </div>

                  <DataGrid
                    rows={rowData}
                    columns={columns}
                    rowCount={totalRecords} 
                    apiRef={apiRef}
                    disableRowSelectionOnClick
                    processRowUpdate={processRowUpdate}
                    ignoreValueFormatterDuringExport
                    disableColumnSelector
                    showToolbar={false}
                    slotProps={{
                      toolbar: {
                        printOptions: { disableToolbarButton: true },
                      },
                    }}
                    getRowId={(row) => row.ID}
                    sx={{
                      height: "76vh",
                      overflowY: "auto",
                      "& .MuiDataGrid-cell": {
                        display: "flex",
                        alignItems: "center",
                      },
                      [`& .${gridClasses.row}.row--removed`]: {
                        backgroundColor: (theme) => {
                        if (theme.palette.mode === 'light') {
                        return 'rgba(255, 170, 170, 0.3)';
                          }
                        return darken('rgba(255, 170, 170, 1)', 0.7);
                        },
                      },
                      [`& .${gridClasses.row}.row--edited`]: {
                        backgroundColor: (theme) => {
                        if (theme.palette.mode === 'light') {
                        return 'rgba(255, 254, 176, 0.3)';
                          }
                        return darken('rgba(255, 254, 176, 1)', 0.6);
                        },
                      },
                    }}
                    loading={isSaving}
                    getRowClassName={getRowClassName}
                    getRowHeight={(params) => {
                      if (expandedRow === params.id) {
                        return 'auto'
                      }
                      return 60; 
                    }}
                    paginationMode="server"
                    pageSizeOptions={[10]}
                    onPaginationModelChange={handlePagination}
                    initialState={{
                      pagination: {
                        paginationModel,
                      },
                    }}
                  />
                </Card>
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Toast
        open={toastOpen}
        toastMsg={toastMsg}
        severity={toastType}
        handleClose={() => setToastOpen(false)}
      />

      <ConfirmationDialog
        open={confirmOpen}
        handleClose={() => setConfirmOpen(false)}
        saveData={saveChanges}
        dialogData={dialogData}
      />
    </>
  );
});

export default BulkEditAssociation;
